import React, { Fragment } from 'react';
import { css } from '@emotion/react';
import AppStoreButtons from '@theme/components/AppStoreButtons';
import { Link } from 'gatsby';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UncontrolledAlert } from 'reactstrap';
import { faMapMarkerAlt, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn
} from '@fortawesome/free-brands-svg-icons';
import Intercom from '@components/Intercom';
import styled from '@emotion/styled';
import { isInternalUrl } from '@lib/urls';

library.add(faFacebookF);
library.add(faTwitter);
library.add(faLinkedinIn);

const SocialIcons = styled.div`
  display: flex;
  text-decoration: none;
  color: white;
  margin-top: 2rem;
  .icon {
    margin-right: 1.5rem;
    border: 1px solid white;
    color: white;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: all 300ms;
    &.facebook:hover {
      background: #3b5998;
      border-color: #3b5998;
    }
    &.twitter:hover {
      background: #1da1f2;
      border-color: #1da1f2;
    }

    &.linkedin:hover {
      background: #0077b5;
      border-color: #0077b5;
    }
  }
`;

const titleStyle = css`
  font-size: 1.25rem;
  margin-top: 2rem;
  margin-bottom: 3rem;
  color: #fff;

  &::after {
    content: "";
    display: block;
    margin-top: 1rem;
    height: 4px;
    width: 50px;
    background: var(--primary);
  }
`;

export default function Footer({ language, layouts }) {

  const { footer } = layouts.find(layout => layout.footer);

  return (
    <Fragment>
      <Intercom language={language} />
      <UncontrolledAlert
        color="light"
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          background: #ffffff;
          position: fixed;
          bottom: 20px;
          right: 0;
          left: 0;
          margin: auto;
          width: 90%;
          max-height: 120px;
          z-index: 3;
        `}
      >
        <img
          css={css`
            width: 100%;
            max-width: 1200px;
            max-height: 100%;
          `}
          src={'http://cms.nospoilers.ai/wp-content/uploads/2021/03/belka_POWER_grafika-1.png'}
          alt={'Banner'}
        />
        <a href="http://nospoilers.ai/pl/projekt-bpr">{'Więcej informacji'}</a>
      </UncontrolledAlert>
      <div
        css={css`
          background: #1d1d1d;
          color: #ffffff;
          padding-bottom: 2rem;
        `}
      >
        <div className="container">
          <div className="row py-5">
            <div className="col-12 col-md-3">
              <h5 css={titleStyle}>{footer.sponsors_label} </h5>
              {footer.logos.map((item, i) => (
                <div className="mb-3" key={`img-footer-logo-${i}`}>
                  <img
                    src={item.logo.local.url}
                    alt={item.logo.alt_text}
                    css={css`
                      /* @md */
                      @media (min-width: 768px) {
                        max-width: 50%;
                      }
                    `}
                  />
                </div>
              ))}
            </div>
            <div className="col-12 col-md-6">
              <h5 css={titleStyle}>{footer.links_label}</h5>
              <div className="row">
                {footer.links.map(({ link }) => {
                  return (
                    <div key={link.url} className="col-12 col-md-6 mb-3">
                      {isInternalUrl(link.url) ? (
                        <Link
                          css={css`
                            color: #fff;
                            opacity: 0.5;
                            &::before {
                              content: ">";
                              padding-right: 0.4rem;
                            }
                            &:hover {
                              opacity: 1;
                              text-decoration: none;
                            }
                          `}
                          to={link.url}
                        >
                          {link.text}
                        </Link>
                      ) : (
                        <a
                          css={css`
                            color: #fff;
                            opacity: 0.5;
                            &::before {
                              content: ">";

                              padding-right: 0.4rem;
                            }
                            &:hover {
                              opacity: 1;
                              text-decoration: none;
                            }
                          `}
                          href={link.url}
                        >
                          {link.text}
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="col-12 col-md-3">
              <h5 css={titleStyle}>{footer.label}</h5>
              <div
                css={css`
                  opacity: 0.5;
                  margin-bottom: 1rem;
                `}
              >
                <FontAwesomeIcon
                  css={css`
                    color: var(--primary);
                    margin-right: 1rem;
                  `}
                  icon={faMapMarkerAlt}
                />
                {footer.address}
              </div>
              <div
                css={css`
                  opacity: 0.5;
                  margin-bottom: 1rem;
                `}
              >
                <FontAwesomeIcon
                  css={css`
                    color: var(--primary);
                    margin-right: 1rem;
                  `}
                  icon={faEnvelope}
                />{' '}
                <a
                  css={css`
                    color: #fff;
                  `}
                  href={`mailto:${footer.contact_email}`}
                >
                  {footer.contact_email}
                </a>
              </div>
              <SocialIcons>
                {footer.contact_channels.map((channel/*, i*/) => {
                  switch (channel.link.link_style) {
                    case 'facebook-f':
                      return (
                        <a key='channel-fb' href={channel.link.link_url} className="icon facebook">
                          {' '}
                          <FontAwesomeIcon icon={['fab', 'facebook-f']} />{' '}
                        </a>
                      );
                    case 'twitter':
                      return (
                        <a key='channel-tt' href={channel.link.link_url} className="icon twitter">
                          {' '}
                          <FontAwesomeIcon icon={['fab', 'twitter']} />{' '}
                        </a>
                      );
                    case 'linkedin-in':
                      return (
                        <a key='channel-li' href={channel.link.link_url} className="icon linkedin">
                          {' '}
                          <FontAwesomeIcon icon={['fab', 'linkedin-in']} />{' '}
                        </a>
                      );

                    default:
                      break;
                  }
                })}
              </SocialIcons>
            </div>
          </div>
        </div>
      </div>
      <div
        css={css`
          padding-top: 1rem;
          padding-bottom: 1rem;
          background: #191919;
          color: #444444;
        `}
      >
        <div className="container">
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              flex-direction: column;
              /* @sm */
              @media (min-width: 576px) {
                flex-direction: row;
              }
            `}
          >
            <span>{footer.subfooter_message}</span>
            <AppStoreButtons links={footer.subfooter_apps} />
            <span>{footer.subfooter_copyright}</span>
          </div>
        </div>
      </div>
    </Fragment>
  );
}